/**
 * Reading a document body (assembled from its blocks — docBlocks.js — or a
 * docbody stored whole), its MCP content hash, and this device's record of
 * its own block writes. Moved out of documentsStore.js (size), which
 * re-exports what callers use.
 */
import { blocksFromRows, isBlockRow } from './docBlocks';
import { compactBlockNode } from './docBodyCodec';
import { headSnapshot, isWholeBody, wholeSnapshot } from './docBodyHead';
import { bodyHtmlCodec } from './bodyHtmlCodec';
import { stableStringify } from '../../../components/ui/lexicalBlocks';

/**
 * A document's body from its rows. Its blocks (docBlocks.js) when it has
 * any, with the HTML generated from them — the `docbody` row's own HTML is
 * only a snapshot refreshed now and then for older app versions. A `docbody`
 * still holding a whole snapshot (`bodyBlocks` / `bodyLexical`) is a body
 * not moved to blocks yet, or one an older app version saved after the move:
 * that is newer, and wins until the next save moves it to blocks again.
 */
export async function assembleBody(rows, body) {
  const blockRows = (rows || []).filter(isBlockRow);
  if (!blockRows.length && body && body.blocks && !isWholeBody(body)) {
    // The head is here, its blocks are not (still syncing, or refused as too
    // large): open the head's own snapshot (real ids, so they line up when the
    // blocks arrive) — or nothing for a page too large for one, never the
    // "update the app" notice as if it were the page.
    if (body.htmlOmitted) return { bodyHtml: '', bodyLexical: null, blockMode: false, missing: true };
    return { bodyHtml: body.bodyHtml || '', bodyLexical: headSnapshot(body), blockMode: false };
  }
  if (!blockRows.length || isWholeBody(body)) {
    return { bodyHtml: (body && body.bodyHtml) || '', bodyLexical: wholeSnapshot(body), blockMode: false };
  }
  const { snapshot } = blocksFromRows(blockRows);
  let bodyHtml;
  try {
    bodyHtml = (await snapshotHtml(snapshot)) ?? ((body && body.bodyHtml) || '');
  } catch {
    // A node this build cannot render (written by a newer app): the stored
    // snapshot is still the body's HTML, a little older at worst. The blocks
    // themselves are untouched and still what the editor opens.
    bodyHtml = (body && body.bodyHtml) || '';
  }
  return { bodyHtml, bodyLexical: snapshot, blockMode: true };
}

/** Editor blocks -> sanitized HTML (bodyHtmlCodec.js); null when no conversion is installed. */
async function snapshotHtml(snapshot) {
  const codec = bodyHtmlCodec();
  return codec ? codec.toHtml(snapshot) : null;
}

/**
 * HTML -> an editor snapshot with the block ids an editor opening this HTML
 * derives (its blockIdSeed); null when no conversion is installed.
 */
export async function snapshotFromHtml(docId, bodyHtml) {
  const codec = bodyHtmlCodec();
  return codec ? codec.fromHtml(docId, bodyHtml || '') : null;
}

/**
 * A short content hash of a document's body, for MCP's `expected_rev`: it
 * changes whenever any block does (the `docbody` row alone no longer does).
 */
export async function documentBodyRev(doc) {
  if (!doc) return null;
  const text = doc.bodyLexical ? stableStringify(doc.bodyLexical) : `html:${doc.bodyHtml || ''}`;
  // Two FNV-1a passes with different offsets: 16 hex, the width of the old
  // per-row rev. Change detection, not security — no WebCrypto needed.
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ text.length;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x01000193 + 0x2000) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

/**
 * This device's recent block writes, by record id (a few per block): the
 * team broadcast hands a save back to the device that made it, possibly after
 * a newer local save of the same block — that echo must not roll it back.
 */
const OWN_BLOCK_WRITES_PER_ID = 6;
const OWN_BLOCK_WRITES_MAX_IDS = 2000;
const ownBlockWrites = new Map();

export function noteOwnBlockWrite(record) {
  const json = stableStringify(record.data.node);
  const list = (ownBlockWrites.get(record.id) || []).filter((j) => j !== json);
  list.push(json);
  if (list.length > OWN_BLOCK_WRITES_PER_ID) list.shift();
  ownBlockWrites.delete(record.id);
  ownBlockWrites.set(record.id, list);
  if (ownBlockWrites.size > OWN_BLOCK_WRITES_MAX_IDS) ownBlockWrites.delete(ownBlockWrites.keys().next().value);
}

/** True when a block record that arrived (node expanded) is one this device wrote recently. */
export function isOwnBlockEcho(recordId, node) {
  const list = ownBlockWrites.get(recordId);
  if (!list || node == null) return false;
  return list.includes(stableStringify(compactBlockNode(node)));
}
